import React from 'react';
import {
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import If from '@components/if/if.tsx';
import Loader from '@components/loader/loader.tsx';
import PaginationComponent from '@components/pagination/pagination.tsx';
import { ICONS } from '@components/icons/icon.tsx';
import setUpForMeIcon from '@assets/icons/setup-for-me.svg';
import emptyIcon from '@assets/icons/empty.svg';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableHeaderCell } from './sortable-header-cell.tsx';
import { DynamicTableProps, TriggerTypeEnum } from '@models/table.model.ts';
import { useDynamicTableLogic } from '@components/dynamic-table/hooks/use-dynamic-table-logic.ts';
import {
  horizontalListSortingStrategy,
  SortableContext
} from '@dnd-kit/sortable';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import { ApiResponse } from '@models/api.model.ts';
import ClearAllFilters from '@components/dynamic-table/components/clear-all-filters/clear-all-filters.tsx';

const DynamicTable = <T extends ApiResponse<any>>(
  props: DynamicTableProps<T>
) => {
  const { t } = useTranslation();

  const {
    orderedColumns,
    sensors,
    handleDragEnd,
    sort,
    search,
    filters,
    betweenDateRangeFilters,
    tableData,
    isLoaded,
    isLoading,
    pagination,
    showSetup,
    showEmpty,
    setupLoading,
    onTriggerAction,
    tableRef,
    handlePageChange,
    handleRowsPerPageChange,
    handleSort,
    handleSearchChange,
    handleFilterChange,
    handleDateRangeFilterChange,
    clearSort,
    clearFilters,
    clearSearch,
    clearDateRangeFilters,
    clearAllFilters
  } = useDynamicTableLogic(props);

  return (
    <div className="w-full h-full flex flex-col justify-between pb-3.5 gap-1">
      {/*sx={{ width: '100%', overflow: 'hidden' }}*/}
      <Paper
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        <ClearAllFilters
          orderedColumns={orderedColumns}
          search={search}
          filters={filters}
          sort={sort}
          betweenDateRangeFilters={betweenDateRangeFilters}
          clearSearch={clearSearch}
          clearFilters={clearFilters}
          clearSort={clearSort}
          clearDateRangeFilters={clearDateRangeFilters}
          clearAllFilters={clearAllFilters}
          extraFilterButtons={props.extraFilterButtons}
          hiddenFilters={props.hiddenFilters}
        />

        <TableContainer
          ref={tableRef}
          className="h-full overflow-y-auto"
          sx={{ flex: 1, minHeight: 0 }}
        >
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToHorizontalAxis]}
          >
            <SortableContext
              items={orderedColumns
                .filter((_, i) => i !== orderedColumns.length - 1)
                .map((col) => col.id)}
              strategy={horizontalListSortingStrategy}
            >
              <Table stickyHeader aria-label="sticky table">
                <TableHead>
                  <TableRow>
                    {orderedColumns.map((col, index) => (
                      <SortableHeaderCell
                        key={col.id}
                        column={col}
                        index={index}
                        sort={sort}
                        columnsLength={orderedColumns.length}
                        onSort={handleSort}
                        onSearch={handleSearchChange}
                        onFilter={handleFilterChange}
                        onDateRangeFilter={handleDateRangeFilterChange}
                        search={search}
                        filters={filters}
                        betweenFilters={betweenDateRangeFilters}
                        clearSearch={clearSearch}
                        clearFilters={clearFilters}
                        clearDateRangeFilters={clearDateRangeFilters}
                        language={props?.language}
                      />
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <If
                    loading={isLoading}
                    condition={isLoaded && tableData?.length > 0}
                    loadingContent={
                      <TableRow>
                        <TableCell
                          colSpan={orderedColumns.length + 1}
                          className="text-center p-4"
                        >
                          <div className="flex justify-center items-center pt-[14px]">
                            <Loader />
                          </div>
                        </TableCell>
                      </TableRow>
                    }
                    emptyContent={
                      <TableRow>
                        <TableCell colSpan={orderedColumns.length + 1}>
                          {showSetup && (
                            <div className="flex flex-col justify-center items-center py-9 gap-6 max-lg-xl:gap-5">
                              <img
                                src={setUpForMeIcon}
                                alt="Setup"
                                className="max-lg-xl:w-[110px] max-h-[300px]"
                              />
                              <p className="text-[var(--blue-4)] text-[30px] opacity-40 font-semibold text-center max-lg-xl:text-[15px]">
                                {t('setup_for_me_content_text')}
                              </p>
                              <Button
                                className="!px-5"
                                variant="contained"
                                color="primary"
                                startIcon={ICONS.plusIcon}
                                loading={setupLoading}
                                onClick={() =>
                                  onTriggerAction?.(TriggerTypeEnum.Setup)
                                }
                              >
                                {t('setup_for_me_button_text')}
                              </Button>
                            </div>
                          )}
                          {showEmpty && (
                            <div className="flex flex-col justify-center items-center py-9 gap-6 max-lg-xl:gap-5">
                              <img
                                src={emptyIcon}
                                alt="Empty"
                                className="max-lg-xl:w-[110px] max-h-[300px]"
                              />
                              <p className="text-[var(--blue-4)] text-[30px] opacity-40 font-semibold text-center max-lg-xl:text-[15px]">
                                {t('empty_content_text')}
                              </p>

                              {!props.hideEmptyButton && (
                                <Button
                                  className="!px-5"
                                  variant="contained"
                                  color="primary"
                                  startIcon={ICONS.plusIcon}
                                  onClick={() =>
                                    onTriggerAction?.(TriggerTypeEnum.Add)
                                  }
                                >
                                  {t('empty_content_button_text')}
                                </Button>
                              )}
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    }
                  >
                    {tableData?.map((row, rowIndex) => (
                      <TableRow
                        key={row.id || rowIndex}
                        onDoubleClick={() => props.onRowDoubleClick?.(row)}
                        style={{
                          cursor: props.onRowDoubleClick ? 'pointer' : 'default'
                        }}
                      >
                        {orderedColumns.map((column, index) => {
                          const isLast = index === orderedColumns.length - 1;
                          const rowBg =
                            rowIndex % 2 === 1
                              ? 'rgba(247, 249, 252, 1)'
                              : 'white';

                          return (
                            <TableCell
                              key={column.id}
                              sx={{
                                ...(isLast
                                  ? {
                                      position: 'sticky',
                                      right: 0,
                                      backgroundColor: rowBg,
                                      zIndex: 1,
                                      boxShadow: '-2px 0 5px rgba(0,0,0,0.05)'
                                    }
                                  : {})
                              }}
                            >
                              <div className="clamp-text">
                                {column?.render(row[column?.field], row)}
                              </div>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </If>
                </TableBody>
              </Table>
            </SortableContext>
          </DndContext>
        </TableContainer>
      </Paper>

      {tableData?.length > 0 && (
        <div className="ml-auto mr-auto">
          <PaginationComponent
            count={pagination.totalPages}
            page={pagination.page}
            rowsPerPage={pagination.rowsPerPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </div>
      )}
    </div>
  );
};

export default DynamicTable;
